import type { ReactNode } from "react";
import { SkyBadge, SkyCard, SkyText, type SkyBadgeColor } from "@skypointcloud/skypoint-ui";
import type { CanaryOutcome, CanaryResultView, CanaryTenantView } from "@shared/types/canary";
import { useCanaries } from "@/hooks/use-canary";
import { ErrorState } from "./ScreenState";
import { formatInstant, instantHoverTitle, zoneCaption } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * Platform settings > General > Canary (#826, `docs/requirements/slo-canary.md` R4.8): each canary
 * tenant's newest daily verdicts, computed by the runner from skyData's own records. The outcome is
 * a word and a colour, never a colour alone; the code says which check failed.
 */

const HEADERS = ["Day", "Outcome", "Code", "Run", "Judged"] as const;

const OUTCOME: Record<CanaryOutcome, { text: string; color: SkyBadgeColor }> = {
  green: { text: "Green", color: "success" },
  failed: { text: "Failed", color: "danger" },
  missed: { text: "Missed", color: "warning" },
};

/** The verdict's code in words, for the cell. */
const CODE_WORDS: Record<CanaryResultView["code"], string> = {
  GREEN: "Every check passed",
  RUN_FAILED: "The run failed",
  RUN_PARTIALLY_SUCCEEDED: "The run partly failed",
  RUN_CANCELLED: "The run was cancelled",
  ROWS_MISMATCH: "A table's row count was wrong",
  DBT_FAILED: "Transformations or tests failed",
  TOO_SLOW: "The run took too long",
  RUN_UNFINISHED: "The run had not finished",
  NO_RUN: "No run started",
};

function CanaryResults({ canary, zone }: { canary: CanaryTenantView; zone: string }): ReactNode {
  if (canary.results.length === 0) {
    return (
      <SkyText size={200} color="muted" block testId="canary-no-results">
        No verdict yet. The first comes once the schedule's first run has finished.
      </SkyText>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse bg-background text-left" data-testid="canary-grid">
        <caption className="px-3 py-2 text-left">
          <SkyText size={200} color="muted">
            The newest verdicts of {canary.tenantDisplayName}
          </SkyText>
        </caption>
        <thead>
          <tr>
            {HEADERS.map((header) => (
              <th
                key={header}
                scope="col"
                className="whitespace-nowrap border-b border-border px-4 py-3 text-sm font-semibold text-foreground-soft"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {canary.results.map((result) => {
            const outcome = OUTCOME[result.outcome];
            return (
              <tr
                key={`${result.day}:${result.runId ?? "none"}`}
                className="border-b border-border hover:bg-muted/50"
                data-testid="canary-result-row"
              >
                <td className="px-3 py-2">
                  <SkyText size={200} font="numeric">
                    {result.day}
                  </SkyText>
                </td>
                <td className="px-3 py-2">
                  <SkyBadge appearance="tint" color={outcome.color}>
                    {outcome.text}
                  </SkyBadge>
                </td>
                <td className="px-3 py-2">
                  <SkyText size={200} title={result.code}>
                    {CODE_WORDS[result.code]}
                  </SkyText>
                </td>
                <td className="px-3 py-2">
                  <SkyText size={200} font="monospace">
                    {result.runId ?? "None"}
                  </SkyText>
                </td>
                <td className="px-3 py-2">
                  <SkyText size={200} font="numeric" title={instantHoverTitle(result.at, zone)}>
                    {formatInstant(result.at, zone, "minutes")}
                  </SkyText>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function CanaryCard() {
  const zone = useDisplayTz();
  const canaries = useCanaries();

  function renderBody(): ReactNode {
    if (canaries.isPending) {
      return (
        <SkyText size={200} color="muted" block role="status">
          Loading the canary…
        </SkyText>
      );
    }
    if (canaries.isError) {
      return (
        <ErrorState
          title="The canary did not load"
          error={canaries.error}
          onRetry={() => void canaries.refetch()}
          testId="canary-error"
        />
      );
    }
    const rows = canaries.data?.canaries ?? [];
    if (rows.length === 0) {
      return (
        <SkyText size={300} color="muted" block testId="canary-empty">
          No canary is set up in this deployment.
        </SkyText>
      );
    }
    return (
      <div className="flex flex-col gap-4">
        {rows.map((canary) => (
          <CanaryResults key={canary.tenantId} canary={canary} zone={zone} />
        ))}
      </div>
    );
  }

  return (
    <SkyCard appearance="outline" testId="canary-card">
      <section className="flex flex-col gap-3 p-1" aria-labelledby="canary-heading">
        <div>
          <SkyText as="h2" size={400} weight="semibold" block id="canary-heading">
            Canary
          </SkyText>
          <SkyText size={200} color="muted" block>
            A Skypoint tenant imports fixed rows every day through the whole path. skyData judges
            each run from its own records: the run, the rows each table loaded, the transformations
            and the time it took. {zoneCaption(zone)}.
          </SkyText>
        </div>
        {renderBody()}
      </section>
    </SkyCard>
  );
}
